import React, {useCallback, useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {Button, RightButtonRow} from '../../../Atoms/Buttons'
import CenteredModal, {WideModalContent} from '../../../Molecules/Modals/Modals'
import {Heading} from '../../../Atoms/Typography'
import {Baseline, Row} from '../../../Layout/Grid'
import {TextButton} from '../../../Atoms/Buttons'
import styled from 'styled-components'
import {colors} from '../../../../sharedComponents/colors'

import {Recipient, alarmsApi} from '../../../../state/rest'

import {Checkbox} from '../../../Atoms/Forms'
import {findIndex, pick} from 'lodash'

import {getRecipientName} from './AlarmSettingsUtils'
import AddRecipientForm from './AddRecipientForm'
import {v4 as uuid} from 'uuid'
import {IconDelete, IconEdit} from '../../../../Assets/Icons/TinyIcons'
import ConfirmModal from '../../../Molecules/Modals/ConfirmModal'

// TODO! make table code more reusable
const TableContainer = styled.div`
  overflow: auto;
  max-height: 30vh;

  table {
    display: table;
    width: 100%;
    margin: 0;
    padding: 0;
    border-collapse: collapse;
    border-collapse: separate;
    border-spacing: 2px;
    border-color: gray;
  }
  thead {
    display: table-header-group;
    vertical-align: middle;
    border-color: inherit;
  }
  tbody {
    display: table-row-group;
    vertical-align: middle;
    border-color: inherit;
  }
  col {
    display: table-column;
  }
  colgroup {
    display: table-column-group;
  }
  tr {
    display: table-row;
    border: 1px solid black;
    border-bottom: 2px solid black;
    padding: 5px;
    border-bottom-width: 1px;
    margin-bottom: 0;
    vertical-align: inherit;
    border-color: inherit;
  }
  table > tr {
    vertical-align: middle;
  }
  th,
  td {
    display: table-cell;
    vertical-align: inherit;
    padding: 10px;
    text-align: left;
  }
  th {
    font-size: 11px;
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 50;
  }
  td {
    display: table-cell;
    text-align: left;
    font-size: 14px;
    border-bottom: none;

    &:last-child {
      border-bottom: none;
    }
  }

  .warn {
    color: ${colors.system.red};
  }
`

const AddRecipientContainer = styled.div`
  hr {
    margin: 1.5rem 0;
    border: 0;
    border-bottom: 1px solid ${colors.system.lightGrey_5};
  }
  > :not(hr) {
    margin: 1rem;
  }
`

const DeleteButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.system.red};
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
`

const ActionCell = styled.td`
  white-space: nowrap;
  text-align: right !important;
`

const ActionButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.brand.cobalt};
  cursor: pointer;
  font-size: 1rem;
  margin-left: 0.5rem;
  padding: 0;

  &:first-child {
    margin-left: 0;
  }
`

interface RecipientOption {
  id: string
  isExt: boolean
  isNew: boolean
  selected: boolean
  recipient: Recipient
}

interface ModalProps {
  isOpen: boolean
  onCancel: () => void
  onSave: (recipients: Recipient[], extRecipients: Recipient[]) => void
  recipients?: Recipient[]
  extRecipients?: Recipient[]
}

const RECIPIENT_PROPS = ['id', 'name', 'firstName', 'lastName', 'email', 'phoneNumber', 'language']

const RecipientModal = ({isOpen, onCancel, onSave, recipients, extRecipients}: ModalProps) => {
  const {t} = useTranslation('alarms')
  const [addVisible, setAddVisible] = useState(false)
  const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>([])
  const [recipientToDelete, setRecipientToDelete] = useState<string | null>(null)
  const [recipientToEdit, setRecipientToEdit] = useState<RecipientOption | null>(null)

  const safeExt = extRecipients ?? []
  const safeRec = recipients ?? []

  const includesRecipient = (recipient: Recipient, list?: Recipient[]) => {
    return (
      findIndex(list || [], r => {
        return r.id === recipient.id
      }) !== -1
    )
  }

  const buildOptions = useCallback(
    (userRecipients: Recipient[]) => {
      const base: Recipient[] = [...userRecipients, ...safeExt]
      return base.map(recipient => ({
        id: recipient.id ?? uuid(),
        isNew: false,
        isExt: includesRecipient(recipient, safeExt),
        selected: includesRecipient(recipient, safeRec) || includesRecipient(recipient, safeExt),
        recipient: recipient
      }))
    },
    [safeExt, safeRec]
  )

  const setup = useCallback(async () => {
    const usersData = await alarmsApi.getRecipients()
    const userRecipients = usersData.items.map(user => {
      return pick(user, RECIPIENT_PROPS) as Recipient
    })
    setRecipientOptions(buildOptions(userRecipients))
  }, [buildOptions])

  useEffect(() => {
    if (!isOpen) return
    setup()
  }, [isOpen, safeRec.length, safeExt.length])

  const toggleRow = useCallback((id: string) => {
    setRecipientOptions(prev => prev.map(opt => (opt.id === id ? {...opt, selected: !opt.selected} : opt)))
  }, [])

  const handleSave = useCallback(() => {
    const nextRecipients = recipientOptions.filter(o => o.selected && !o.isExt).map(o => o.recipient)
    const nextExtRecipients = recipientOptions.filter(o => o.selected && o.isExt).map(o => o.recipient)
    onSave?.(nextRecipients, nextExtRecipients)
  }, [onSave, recipientOptions])

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel()
    }
  }, [onCancel])

  const handleAddExtRecipient = useCallback(
    (recipient: Recipient) => {
      if (recipientToEdit) {
        setRecipientOptions(prev =>
          prev.map(option => (option.id === recipientToEdit.id ? {...option, recipient} : option))
        )
        setRecipientToEdit(null)
      } else {
        setRecipientOptions(prev => [
          ...prev,
          {
            id: uuid(),
            isNew: true,
            isExt: true,
            selected: true,
            recipient
          }
        ])
      }
      setAddVisible(false)
    },
    [recipientToEdit]
  )

  const removeExternalRecipient = useCallback((id: string) => {
    setRecipientOptions(prev => prev.filter(option => option.id !== id))
    setRecipientToDelete(null)
  }, [])

  const startEditing = useCallback((option: RecipientOption) => {
    setRecipientToEdit(option)
    setAddVisible(true)
  }, [])

  const cancelRecipientForm = useCallback(() => {
    setRecipientToEdit(null)
    setAddVisible(false)
  }, [])

  return (
    <CenteredModal isOpen={isOpen} data-cy="recipient-modal">
      <WideModalContent>
        <Baseline>
          <Heading level={3}>{t('alarms:labels.selectRecipients', 'Select recipients for alarm messages')}</Heading>
          <TableContainer>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>SMS</th>
                  <th>Email</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recipientOptions.map((option: RecipientOption, index: number) => {
                  return (
                    <tr key={option.id}>
                      <td>
                        <Checkbox name={option.id} checked={option.selected} onClick={() => toggleRow(option.id)} />
                      </td>
                      <td>
                        {getRecipientName(option.recipient) ? (
                          getRecipientName(option.recipient)
                        ) : (
                          <span className="warn">{`(${t('alarms:labels.noName', 'no name')})`}</span>
                        )}
                      </td>
                      <td>
                        {option.recipient.phoneNumber ? (
                          option.recipient.phoneNumber
                        ) : (
                          <span className="warn">{`(${t('alarms:labels.noNumber', 'no number')})`}</span>
                        )}
                      </td>
                      <td>
                        {option.recipient.email ? (
                          option.recipient.email
                        ) : (
                          <span className="warn">{`(${t('alarms:labels.noAddress', 'no address')})`}</span>
                        )}
                      </td>
                      <ActionCell>
                        {option.isExt && (
                          <>
                            <ActionButton
                              type="button"
                              aria-label={t('alarms:actions.editRecipient', 'Edit recipient')}
                              onClick={() => startEditing(option)}
                            >
                              <IconEdit />
                            </ActionButton>
                            <DeleteButton
                              type="button"
                              aria-label={t('alarms:actions.removeRecipient', 'Remove recipient')}
                              onClick={() => setRecipientToDelete(option.id)}
                            >
                              <IconDelete />
                            </DeleteButton>
                          </>
                        )}
                      </ActionCell>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableContainer>
        </Baseline>
        {!addVisible ? (
          <Row margin={'1.5rem 0'}>
            <TextButton
              onClick={() => {
                setRecipientToEdit(null)
                setAddVisible(s => !s)
              }}
            >
              {t('alarms:actions.addNewRecipient', 'Add new recipient')}
            </TextButton>
          </Row>
        ) : (
          <AddRecipientContainer>
            <hr />
            <div>
              <AddRecipientForm
                initialRecipient={recipientToEdit?.recipient}
                onCancel={cancelRecipientForm}
                onAdd={handleAddExtRecipient}
              />
            </div>
            <hr />
          </AddRecipientContainer>
        )}
        <Baseline>
          <RightButtonRow>
            <Button variant="secondary" negative onClick={() => handleCancel()}>
              {t('common:buttons.cancel', 'Cancel')}
            </Button>
            <Button variant="secondary" onClick={() => handleSave()}>
              {t('common:buttons.save', 'Save')}
            </Button>
          </RightButtonRow>
        </Baseline>
      </WideModalContent>
      <ConfirmModal
        heading={t('alarms:confirmation.deleteRecipientTitle', 'Delete recipient')}
        content={t('alarms:confirmation.deleteRecipientDescription', 'Are you sure you want to delete recipient?')}
        actions={[
          {label: t('common:actions.cancel', 'Cancel'), action: () => setRecipientToDelete(null)},
          {
            label: t('common:actions.delete', 'Delete'),
            action: () => recipientToDelete && removeExternalRecipient(recipientToDelete)
          }
        ]}
        isOpen={recipientToDelete !== null}
        closeModal={() => setRecipientToDelete(null)}
      />
    </CenteredModal>
  )
}

export default RecipientModal
